import { useCallback, useEffect, useRef, useState } from "react";

export type SpeechLang = "ca-ES" | "es-ES";

/** Velocitat de lectura del TTS (1 = normal). Una mica més ràpida del normal. */
const SPEECH_RATE = 1.5;

// --- Tipus mínims de la Web Speech API ---------------------------------------
// `SpeechRecognition` no forma part de lib.dom, així que el declarem aquí.
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onend: ((e: Event) => void) | null;
  onerror: ((e: Event) => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Treu el markdown bàsic perquè el TTS no llegeixi asteriscos ni guions. */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

export interface UseSpeech {
  recognitionSupported: boolean;
  listening: boolean;
  /** Comença a dictar. `onTranscript` rep el text (parcial i final). */
  startListening: (onTranscript: (text: string, isFinal: boolean) => void) => void;
  stopListening: () => void;

  synthSupported: boolean;
  /** Id del missatge que s'està llegint, o `null`. */
  speakingId: string | null;
  speak: (text: string, id?: string) => void;
  stopSpeaking: () => void;
}

/**
 * Reconeixement de veu (dictat) i síntesi de veu (lectura) amb la Web Speech
 * API gratuïta del navegador. `lang` controla l'idioma d'ambdós (ca-ES / es-ES).
 */
export function useSpeech(lang: SpeechLang): UseSpeech {
  const langRef = useRef(lang);
  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  // --- Reconeixement (dictat) ---
  const recognitionSupported = getRecognitionCtor() !== null;
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);

  const stopListening = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const startListening = useCallback(
    (onTranscript: (text: string, isFinal: boolean) => void) => {
      const Ctor = getRecognitionCtor();
      if (!Ctor) return;
      recRef.current?.abort();

      const rec = new Ctor();
      rec.lang = langRef.current;
      rec.continuous = false; // una sola intervenció: para sola en acabar de parlar
      rec.interimResults = true; // feedback en directe mentre parla

      let finalText = "";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        onTranscript(`${finalText}${interim}`.trim(), false);
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => {
        setListening(false);
        recRef.current = null;
        if (finalText.trim()) onTranscript(finalText.trim(), true);
      };

      recRef.current = rec;
      try {
        rec.start();
        setListening(true);
      } catch {
        setListening(false);
      }
    },
    []
  );

  // --- Síntesi (lectura) ---
  const synthSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const [speakingId, setSpeakingId] = useState<string | null>(null);

  // Algunes plataformes carreguen les veus de manera asíncrona.
  useEffect(() => {
    if (!synthSupported) return;
    const warm = () => window.speechSynthesis.getVoices();
    warm();
    window.speechSynthesis.addEventListener?.("voiceschanged", warm);
    return () =>
      window.speechSynthesis.removeEventListener?.("voiceschanged", warm);
  }, [synthSupported]);

  const pickVoice = useCallback((l: string): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();
    const base = l.split("-")[0].toLowerCase();
    return (
      voices.find((v) => v.lang === l) ??
      voices.find((v) => v.lang?.toLowerCase().startsWith(base)) ??
      null
    );
  }, []);

  const stopSpeaking = useCallback(() => {
    if (!synthSupported) return;
    window.speechSynthesis.cancel();
    setSpeakingId(null);
  }, [synthSupported]);

  const speak = useCallback(
    (text: string, id?: string) => {
      if (!synthSupported) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(stripMarkdown(text));
      u.lang = langRef.current;
      u.rate = SPEECH_RATE;
      const voice = pickVoice(langRef.current);
      if (voice) u.voice = voice;
      u.onend = () => setSpeakingId(null);
      u.onerror = () => setSpeakingId(null);
      setSpeakingId(id ?? "");
      window.speechSynthesis.speak(u);
    },
    [pickVoice, synthSupported]
  );

  // Neteja en desmuntar: para dictat i lectura (p. ex. en tancar la finestra).
  useEffect(() => {
    return () => {
      recRef.current?.abort();
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return {
    recognitionSupported,
    listening,
    startListening,
    stopListening,
    synthSupported,
    speakingId,
    speak,
    stopSpeaking,
  };
}
